import { isNullish } from '../Nullish';
import { EventEmitterInit, EventEmitterListener, IEventEmitter } from './types';
import { emitEvent } from './utils';

/**
 * Use when you need to be able to change the emitter element.
 */
export class DynamicEventEmitter implements IEventEmitter {
    #eventEmitterElement?: EventTarget;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    #listeners: Array<{ type: string; listener: EventEmitterListener<any> }>;

    constructor() {
        this.#listeners = [];
    }

    /**
     * Set the `HTMLElement` that will be used to listen/emit events.
     *
     * @param emitterElement
     */
    setEventEmitterElement(emitterElement: EventTarget): void {
        if (!isNullish(this.#eventEmitterElement)) {
            const oldEmitterElement = this.#eventEmitterElement;
            this.#eventEmitterElement = undefined;
            // remove listeners
            this.#listeners.forEach((i) => {
                oldEmitterElement.removeEventListener(i.type, i.listener);
            });
        }

        // add listeners
        this.#listeners.forEach((i) => {
            emitterElement.addEventListener(i.type, i.listener);
        });

        this.#eventEmitterElement = emitterElement;
    }

    // inheritDoc
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addListener(type: string, listener: EventEmitterListener<any>): void {
        this.#listeners.push({
            type,
            listener,
        });

        if (!isNullish(this.#eventEmitterElement)) {
            this.#eventEmitterElement.addEventListener(type, listener);
        }
    }

    // inheritDoc
    emitEvent(type: string, detail: unknown = null, customEventInit?: EventEmitterInit): boolean {
        if (isNullish(this.#eventEmitterElement)) {
            throw new TypeError('Emitter has not been defined.');
        }

        return emitEvent(this, this.#eventEmitterElement, type, detail, customEventInit);
    }
}
